import { useEffect, useState } from 'react';
import { Pencil, Mail, Save } from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  FormField,
  StatusBadge,
  NotificationCard,
  UniversalLoadingState,
  useToast,
} from '@afrilink/ui';
import { adminSettingsService } from '@/domains/settings/services/adminSettingsService';
import { useAuth } from '@/shared/contexts/AuthContext';

type Template = Awaited<ReturnType<typeof adminSettingsService.listEmailTemplates>>[number];

const TEMPLATE_DESCRIPTIONS: Record<string, string> = {
  welcome: 'Sent when a new customer is created.',
  claim_existing: 'Sent to a pre-imported cPanel customer to claim their portal account.',
  password_reset: 'Sent when a customer (or staff) requests a password reset.',
  invoice_issued: 'Sent when a renewal or one-off invoice is issued.',
  invoice_grace_started: 'Sent when an invoice transitions to grace period (or via bulk reminders).',
  invoice_paid: 'Sent when a payment marks an invoice paid.',
  account_suspended: 'Sent when a hosting account is suspended for non-payment.',
  account_unsuspended: 'Sent when a suspended account is restored after payment.',
  cancellation_confirmed: 'Sent when a cancellation request is scheduled.',
  domain_registered: 'Sent when a domain registration completes.',
  domain_expiring: 'Sent when a domain is approaching expiry.',
  domain_renewed: 'Sent when a domain renewal completes.',
  ticket_reply: 'Sent when staff replies to a support ticket.',
  popia_export_ready: 'Sent when a POPIA data export is ready (currently inline, not used).',
  ops_alert: 'Sent to on-call staff for system alerts (e.g. failed payment saga).',
  marketing_contact_internal: 'Internal — delivered to support@ when a marketing form is submitted.',
};

export default function EmailTemplatesPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const role = (user?.app_metadata as { role?: string } | undefined)?.role;
  const isStaff = role === 'staff' || role === 'admin' || role === 'super_admin';

  const [rows, setRows] = useState<Template[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Template | null>(null);

  function reload() {
    adminSettingsService.listEmailTemplates().then(setRows).catch((e: unknown) => {
      setError(e instanceof Error ? e.message : String(e));
    });
  }

  useEffect(reload, []);

  return (
    <div className="space-y-4">
      <Card interactive={false} className="border-info/30 bg-info/5 p-4 text-sm">
        <p>
          Templates use <code>{'{{variable}}'}</code> placeholders that the notification-runner
          substitutes from the per-event payload. Editing creates a new version (the previous
          version is deactivated but kept for rollback). The React Email layer wraps the markdown
          body in branded HTML — edit the body for content, leave layout to React Email.
        </p>
      </Card>

      {error && <NotificationCard tone="danger" title="Could not load templates" body={error} />}

      <Card interactive={false}>
        {!rows ? (
          <UniversalLoadingState variant="spinner" caption="Loading…" />
        ) : (
          <div className="divide-y divide-border">
            {rows.map((t) => (
              <div key={t.id} className="grid grid-cols-1 items-start gap-3 p-4 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    <code className="text-sm font-semibold text-foreground">
                      {t.template_key}
                    </code>
                    <StatusBadge tone="info">v{t.version}</StatusBadge>
                  </div>
                  {TEMPLATE_DESCRIPTIONS[t.template_key] && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {TEMPLATE_DESCRIPTIONS[t.template_key]}
                    </p>
                  )}
                </div>
                <div className="sm:col-span-6">
                  <p className="text-sm font-medium text-foreground">Subject:</p>
                  <code className="text-xs text-muted-foreground break-all">{t.subject}</code>
                </div>
                <div className="flex justify-end sm:col-span-2">
                  <Button variant="outline" size="sm" disabled={!isStaff} onClick={() => setEditing(t)}>
                    <Pencil className="h-3.5 w-3.5" /> Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {editing && (
        <EditModal
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            toast({ tone: 'success', title: 'Template saved', body: `${editing.template_key} → v${editing.version + 1}` });
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
}

function EditModal({
  template,
  onClose,
  onSaved,
}: {
  template: Template;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { toast } = useToast();
  const [subject, setSubject] = useState(template.subject);
  const [body, setBody] = useState(template.body_markdown);
  const [saving, setSaving] = useState(false);

  // Extract {{var}} placeholders for the help text
  const placeholders = Array.from(new Set(
    [subject, body]
      .join(' ')
      .match(/\{\{\s*[\w.]+\s*\}\}/g) ?? [],
  ));

  async function save() {
    if (!subject.trim()) {
      toast({ tone: 'danger', title: 'Subject required' });
      return;
    }
    if (!body.trim()) {
      toast({ tone: 'danger', title: 'Body required' });
      return;
    }
    setSaving(true);
    try {
      await adminSettingsService.updateEmailTemplate({
        template_key: template.template_key,
        current_version: template.version,
        subject: subject.trim(),
        body_markdown: body,
      });
      onSaved();
    } catch (e) {
      toast({ tone: 'danger', title: 'Save failed', body: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={true}
      onOpenChange={(v) => !v && onClose()}
      title={`Edit ${template.template_key}`}
      description={`Saving creates v${template.version + 1}; v${template.version} stays in DB for rollback.`}
      size="wide"
    >
      <ModalBody>
        <div className="space-y-3">
          <FormField label="Subject" htmlFor="t-subject">
            <Input
              id="t-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={saving}
              spellCheck={false}
            />
          </FormField>
          <FormField label="Body (markdown)" htmlFor="t-body" helper="Plain markdown — React Email wraps in branded HTML.">
            <textarea
              id="t-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={saving}
              rows={12}
              spellCheck={false}
              className="w-full rounded border border-input bg-card p-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            />
          </FormField>
          {placeholders.length > 0 && (
            <div className="rounded bg-muted p-3 text-xs">
              <p className="font-semibold text-foreground">Placeholders detected:</p>
              <p className="mt-1 text-muted-foreground">{placeholders.join(' · ')}</p>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={save} loading={saving} disabled={saving}>
          <Save className="h-4 w-4" /> Save as v{template.version + 1}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
